import { motion } from "framer-motion";
import clsx from "clsx";

import { View, Text } from "../base";
import { Icon } from "../base/Icon";
import { useSTTProviders } from "../../hooks/useSTTProviders";
import { AgentDeployment, DeploymentSTTConfig } from "../../redux/reducers/agentDeploymentSlice";

interface DeploymentComparisonProps {
  leftDeployment: AgentDeployment;
  rightDeployment: AgentDeployment;
  onClose?: () => void;
  className?: string;
  getFunctionInfo?: (environmentId: string, deployment?: AgentDeployment) => { name: string; version?: string } | null;
  getEnvironmentName?: (environmentId: string) => string;
}

type ComparisonValue = {
  left: any;
  right: any;
  isDifferent: boolean;
};

export const DeploymentComparison = ({
  leftDeployment,
  rightDeployment,
  onClose,
  className,
  getFunctionInfo,
  getEnvironmentName,
}: DeploymentComparisonProps) => {
  const { getModelByValue, loading } = useSTTProviders();

  if (loading) {
    return (
      <View className="flex items-center justify-center p-8">
        <Text className="text-sm text-gray-500">Loading...</Text>
      </View>
    );
  }

  // Helper to check if a field is supported by the model
  const isFieldSupported = (sttModel: string, fieldName: string): boolean => {
    const model = getModelByValue(sttModel);
    const capabilities = model?.capabilities;
    if (!capabilities) return false;

    const supportMap: Record<string, keyof typeof capabilities> = {
      sttLanguage: "languages",
      sttPrompt: "supportsPrompt",
      sttEndpointing: "supportsEndpointing",
      eotThreshold: "supportsEOT",
      eotTimeoutMs: "supportsEOT",
      eagerEotThreshold: "supportsEagerEOT",
      vadMode: "supportsVADMode",
      vadEagerness: "supportsVADEagerness",
      sttNoiseReductionType: "supportsNoiseReduction",
      sttFilter: "supportsFilter",
      sttAutoSwitch: "supportsAutoSwitch",
      sttPrewarmModel: "supportsPrewarm",
    };

    const supportKey = supportMap[fieldName];
    if (!supportKey) return true; // Show fields that aren't in the support map

    const supportValue = capabilities[supportKey];
    if (Array.isArray(supportValue)) {
      return supportValue.length > 0;
    }
    return Boolean(supportValue);
  };

  const compareValues = (left: any, right: any): ComparisonValue => {
    const isDifferent = JSON.stringify(left) !== JSON.stringify(right);
    return { left, right, isDifferent };
  };

  const compareSTTConfig = (left: DeploymentSTTConfig, right: DeploymentSTTConfig) => {
    const fields = [
      // Core STT
      "sttModel",
      "sttLanguage",
      "sttPrompt",
      // Turn Detection & Timing
      "sttEndpointing",
      "eotThreshold",
      "eotTimeoutMs",
      "eagerEotThreshold",
      // VAD (Voice Activity Detection)
      "vadMode",
      "vadEagerness",
      // Audio Processing
      "sttNoiseReductionType",
      "sttFilter",
      "audioInputDelay",
      // Advanced Features
      "sttAutoSwitch",
      "sttPrewarmModel",
      // Environment Settings
      "moduleRule",
      "isDebug",
      // Error Fallback Configuration
      "errorFallbackType",
      "errorFallbackValue",
      "errorFallbackTimeout",
    ] as const;

    return fields.reduce(
      (acc, field) => {
        acc[field] = compareValues(left[field], right[field]);
        return acc;
      },
      {} as Record<string, ComparisonValue>,
    );
  };

  const compareEnvironmentVariables = (left: Record<string, string>, right: Record<string, string>) => {
    const allKeys = new Set([...Object.keys(left), ...Object.keys(right)]);
    return Array.from(allKeys).reduce(
      (acc, key) => {
        acc[key] = compareValues(left[key] || undefined, right[key] || undefined);
        return acc;
      },
      {} as Record<string, ComparisonValue>,
    );
  };

  const sttComparison = compareSTTConfig(leftDeployment.sttConfig, rightDeployment.sttConfig);
  const envVarComparison = compareEnvironmentVariables(leftDeployment.environmentVariables, rightDeployment.environmentVariables);

  const renderValue = (value: any, isDifferent: boolean, position: "left" | "right", fieldName?: string, sttModel?: string) => {
    // Check if field is supported by the model
    if (fieldName && sttModel && !isFieldSupported(sttModel, fieldName)) {
      return <Text className="text-gray-400 dark:text-gray-500 italic">n/a</Text>;
    }

    if (value === undefined || value === null) {
      return <Text className="text-gray-400 dark:text-gray-500 italic">Not set</Text>;
    }

    let colorClass = "text-gray-900 dark:text-white";
    if (isDifferent) {
      colorClass = position === "left" ? "text-red-600 dark:text-red-400 font-medium" : "text-green-600 dark:text-green-400 font-medium";
    }

    return <Text className={clsx("font-mono text-sm", colorClass)}>{typeof value === "boolean" ? value.toString() : value}</Text>;
  };

  const compareDeploymentMetadata = () => {
    return {
      deployedBy: compareValues(leftDeployment.deployedBy, rightDeployment.deployedBy),
      deploymentType: compareValues(leftDeployment.deploymentType, rightDeployment.deploymentType),
      status: compareValues(leftDeployment.status, rightDeployment.status),
    };
  };

  const metadataComparison = compareDeploymentMetadata();

  const renderComparisonRow = (field: string, comparison: ComparisonValue, formatValue?: (value: any, field: string) => any) => {
    const leftValue = formatValue ? formatValue(comparison.left, field) : comparison.left;
    const rightValue = formatValue ? formatValue(comparison.right, field) : comparison.right;

    return (
      <View key={field}>
        {/* Desktop Layout */}
        <View
          className={clsx(
            "hidden md:grid grid-cols-3 gap-4 py-2 border-b border-gray-100 dark:border-gray-600",
            comparison.isDifferent && "bg-gray-50 dark:bg-gray-700/30 -mx-2 px-2 rounded",
          )}
        >
          <Text className="text-sm font-medium text-gray-500 dark:text-gray-400">
            {field.startsWith("stt") || field === "moduleRule" || field === "isDebug" || field.startsWith("error")
              ? getSTTFieldLabel(field)
              : getMetadataFieldLabel(field)}
          </Text>
          <View className="text-right">{renderValue(leftValue, comparison.isDifferent, "left")}</View>
          <View className="text-right">{renderValue(rightValue, comparison.isDifferent, "right")}</View>
        </View>

        {/* Mobile Layout */}
        <View className="md:hidden">
          <Text className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
            {field.startsWith("stt") || field === "moduleRule" || field === "isDebug" || field.startsWith("error")
              ? getSTTFieldLabel(field)
              : getMetadataFieldLabel(field)}
          </Text>
          <View className={clsx("space-y-2 p-3 rounded", comparison.isDifferent && "bg-gray-50 dark:bg-gray-700/30")}>
            <View className="flex justify-between items-center">
              <Text className="text-xs text-gray-500 dark:text-gray-400">Older:</Text>
              {renderValue(leftValue, comparison.isDifferent, "left")}
            </View>
            <View className="flex justify-between items-center">
              <Text className="text-xs text-gray-500 dark:text-gray-400">Newer:</Text>
              {renderValue(rightValue, comparison.isDifferent, "right")}
            </View>
          </View>
        </View>
      </View>
    );
  };

  const getMetadataFieldLabel = (field: string): string => {
    const labels: Record<string, string> = {
      deployedBy: "Deployed By",
      deploymentType: "Deployment Type",
      status: "Status",
    };
    return labels[field] || field;
  };

  const getSTTFieldLabel = (field: string): string => {
    const labels: Record<string, string> = {
      // Core STT
      sttModel: "STT Model",
      sttLanguage: "STT Language",
      sttPrompt: "STT Prompt",
      // Turn Detection & Timing
      sttEndpointing: "STT Endpointing (ms)",
      eotThreshold: "EOT Threshold",
      eotTimeoutMs: "EOT Timeout (ms)",
      eagerEotThreshold: "Eager EOT Threshold",
      // VAD (Voice Activity Detection)
      vadMode: "VAD Mode",
      vadEagerness: "VAD Eagerness",
      // Audio Processing
      sttNoiseReductionType: "Noise Reduction Type",
      sttFilter: "STT Filter",
      audioInputDelay: "Audio Input Delay (ms)",
      // Advanced Features
      sttAutoSwitch: "Auto Language Switch",
      sttPrewarmModel: "Prewarm Model",
      // Environment Settings
      moduleRule: "Module Rule",
      isDebug: "Debug Mode",
      // Error Fallback Configuration
      errorFallbackType: "Error Fallback Type",
      errorFallbackValue: "Error Fallback Value",
      errorFallbackTimeout: "Error Fallback Timeout (ms)",
    };
    return labels[field] || field.replace(/([A-Z])/g, " $1").toLowerCase();
  };

  const hasAnyDifferences =
    Object.values(sttComparison).some((c) => c.isDifferent) ||
    Object.values(envVarComparison).some((c) => c.isDifferent) ||
    Object.values(metadataComparison).some((c) => c.isDifferent) ||
    leftDeployment.id !== rightDeployment.id;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className={clsx("bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6", className)}
    >
      {/* Summary */}
      <View className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <View className="flex items-center gap-2 mb-2">
          <Icon
            name={hasAnyDifferences ? "info" : "check-circle"}
            size={16}
            className={hasAnyDifferences ? "text-blue-500" : "text-green-500"}
          />
          <Text className="font-medium text-gray-900 dark:text-white">{hasAnyDifferences ? "Differences Found" : "No Differences"}</Text>
          <Text className="text-sm text-gray-600 dark:text-gray-300">
            {hasAnyDifferences ? "The deployments have configuration differences highlighted below." : "These deployments have identical configurations."}
          </Text>
        </View>
      </View>

      {/* Deployment Headers */}
      <View className="mb-6 sticky top-0 bg-white dark:bg-gray-800 z-10 -mx-6 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        {/* Desktop Headers */}
        <View className="hidden md:grid grid-cols-3 gap-6">
          <View className="text-end"></View>
          <View className="text-end">
            <View className="flex items-center justify-end gap-2 mb-2">
              <View className="px-2 py-1 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 rounded text-xs font-medium">Comparing Against</View>
            </View>
            <View className="flex items-center justify-end gap-2 mb-1">
              <Text className="text-lg font-medium text-gray-900 dark:text-white mr-2">{`deployment-${leftDeployment.id}`}</Text>
              {leftDeployment.isActive && (
                <View className="px-2 py-1 bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400 rounded-full text-xs font-medium">Active</View>
              )}
            </View>
            <Text className="text-sm text-gray-500 dark:text-gray-400 mb-1 mr-2">{new Date(leftDeployment.deployedAt).toLocaleString()}</Text>
            {getEnvironmentName && (
              <Text className="text-xs text-gray-500 dark:text-gray-400">Environment: {getEnvironmentName(leftDeployment.environmentId)}</Text>
            )}
          </View>
          <View className="text-end">
            <View className="flex items-center justify-end gap-2 mb-2">
              <View className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded text-xs font-medium">
                Currently Viewing
              </View>
            </View>
            <View className="flex items-center justify-end gap-2 mb-1">
              <Text className="text-lg font-medium text-gray-900 dark:text-white mr-2">{`deployment-${rightDeployment.id}`}</Text>
              {rightDeployment.isActive && (
                <View className="px-2 py-1 bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400 rounded-full text-xs font-medium">Active</View>
              )}
            </View>
            <Text className="text-sm text-gray-500 dark:text-gray-400 mb-1 mr-2">{new Date(rightDeployment.deployedAt).toLocaleString()}</Text>
            {getEnvironmentName && (
              <Text className="text-xs text-gray-500 dark:text-gray-400">Environment: {getEnvironmentName(rightDeployment.environmentId)}</Text>
            )}
          </View>
        </View>

        {/* Mobile Headers */}
        <View className="md:hidden space-y-4">
          <View className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <View className="flex-1">
              <View className="flex items-center gap-2 mb-2">
                <View className="px-2 py-1 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 rounded text-xs font-medium">Comparing Against</View>
                {leftDeployment.isActive && (
                  <View className="px-2 py-1 bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400 rounded-full text-xs font-medium">Active</View>
                )}
              </View>
              <Text className="text-lg font-medium text-gray-900 dark:text-white mr-2">{leftDeployment.id}</Text>
              <Text className="text-xs text-gray-500 dark:text-gray-400 mb-1">{new Date(leftDeployment.deployedAt).toLocaleString()}</Text>
              {getEnvironmentName && (
                <Text className="text-xs text-gray-500 dark:text-gray-400">Environment: {getEnvironmentName(leftDeployment.environmentId)}</Text>
              )}
            </View>
          </View>
          <View className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <View className="flex-1">
              <View className="flex items-center gap-2 mb-2">
                <View className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded text-xs font-medium">
                  Currently Viewing
                </View>
                {rightDeployment.isActive && (
                  <View className="px-2 py-1 bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400 rounded-full text-xs font-medium">Active</View>
                )}
              </View>
              <Text className="text-lg font-medium text-gray-900 dark:text-white mr-2">{rightDeployment.id}</Text>
              <Text className="text-xs text-gray-500 dark:text-gray-400 mb-1">{new Date(rightDeployment.deployedAt).toLocaleString()}</Text>
              {getEnvironmentName && (
                <Text className="text-xs text-gray-500 dark:text-gray-400">Environment: {getEnvironmentName(rightDeployment.environmentId)}</Text>
              )}
            </View>
          </View>
        </View>
      </View>

      {/* Basic Info Comparison */}
      <View className="mb-6">
        <Text className="text-lg font-medium text-gray-900 dark:text-white mb-4">Basic Information</Text>
        <View className="space-y-3">
          {/* Desktop Layout */}
          <View className="hidden md:grid grid-cols-3 gap-4 py-2 border-b border-gray-100 dark:border-gray-600">
            <Text className="text-sm font-medium text-gray-500 dark:text-gray-400">Deployment ID</Text>
            <View className="text-right">{renderValue(leftDeployment.id, leftDeployment.id !== rightDeployment.id, "left")}</View>
            <View className="text-right">{renderValue(rightDeployment.id, leftDeployment.id !== rightDeployment.id, "right")}</View>
          </View>

          {/* Mobile Layout */}
          <View className="md:hidden space-y-3">
            <Text className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Deployment ID</Text>
            <View className={clsx("space-y-2", leftDeployment.id !== rightDeployment.id && "bg-gray-50 dark:bg-gray-700/30 p-3 rounded")}>
              <View className="flex justify-between items-center">
                <Text className="text-xs text-gray-500 dark:text-gray-400">Older:</Text>
                {renderValue(leftDeployment.id, leftDeployment.id !== rightDeployment.id, "left")}
              </View>
              <View className="flex justify-between items-center">
                <Text className="text-xs text-gray-500 dark:text-gray-400">Newer:</Text>
                {renderValue(rightDeployment.id, leftDeployment.id !== rightDeployment.id, "right")}
              </View>
            </View>
          </View>
        </View>
      </View>

      {/* Deployment Metadata Comparison */}
      <View className="mb-6">
        <Text className="text-lg font-medium text-gray-900 dark:text-white mb-4">Deployment Metadata</Text>
        <View className="space-y-3">
          {Object.entries(metadataComparison).map(([field, comparison]) => {
            // Skip if both values are undefined/null
            if (comparison.left === undefined && comparison.right === undefined) return null;

            const formatValue = (value: any, field: string) => {
              if (field === "deployedAt") {
                return value ? new Date(value).toLocaleString() : value;
              }
              if (field === "deploymentType" && Array.isArray(value)) {
                return value.join(", ");
              }
              return value;
            };

            return renderComparisonRow(field, comparison, formatValue);
          })}
        </View>
      </View>

      {/* STT & Environment Configuration Comparison */}
      <View className="mb-6">
        <Text className="text-lg font-medium text-gray-900 dark:text-white mb-4">STT & Environment Configuration</Text>
        <View className="space-y-3">
          {Object.entries(sttComparison).map(([field, comparison]) => {
            if (comparison.left === undefined && comparison.right === undefined) return null;

            // Custom STT field rendering with capability checking
            const leftValue = comparison.left;
            const rightValue = comparison.right;
            return (
              <View key={field}>
                {/* Desktop Layout */}
                <View
                  className={clsx(
                    "hidden md:grid grid-cols-3 gap-4 py-2 border-b border-gray-100 dark:border-gray-600",
                    comparison.isDifferent && "bg-gray-50 dark:bg-gray-700/30 -mx-2 px-2 rounded",
                  )}
                >
                  <Text className="text-sm font-medium text-gray-500 dark:text-gray-400">{getSTTFieldLabel(field)}</Text>
                  <View className="text-right">{renderValue(leftValue, comparison.isDifferent, "left", field, leftDeployment.sttConfig.sttModel)}</View>
                  <View className="text-right">{renderValue(rightValue, comparison.isDifferent, "right", field, rightDeployment.sttConfig.sttModel)}</View>
                </View>

                {/* Mobile Layout */}
                <View className="md:hidden">
                  <Text className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">{getSTTFieldLabel(field)}</Text>
                  <View className={clsx("space-y-3 p-3 rounded", comparison.isDifferent && "bg-gray-50 dark:bg-gray-700/30")}>
                    <View className="flex justify-between items-center">
                      <Text className="text-xs text-gray-500 dark:text-gray-400">Older:</Text>
                      {renderValue(leftValue, comparison.isDifferent, "left", field, leftDeployment.sttConfig.sttModel)}
                    </View>
                    <View className="flex justify-between items-center">
                      <Text className="text-xs text-gray-500 dark:text-gray-400">Newer:</Text>
                      {renderValue(rightValue, comparison.isDifferent, "right", field, rightDeployment.sttConfig.sttModel)}
                    </View>
                  </View>
                </View>
              </View>
            );
          })}
        </View>
      </View>

      {/* Environment Variables Comparison */}
      {Object.keys(envVarComparison).length > 0 && (
        <View className="mb-6">
          <Text className="text-lg font-medium text-gray-900 dark:text-white mb-4">Environment Variables</Text>
          <View className="space-y-3">
            {Object.entries(envVarComparison).map(([key, comparison]) => (
              <View key={key}>
                {/* Desktop Layout */}
                <View
                  className={clsx(
                    "hidden md:grid grid-cols-3 gap-4 py-2 border-b border-gray-100 dark:border-gray-600",
                    comparison.isDifferent && "bg-gray-50 dark:bg-gray-700/30 -mx-2 px-2 rounded",
                  )}
                >
                  <Text className="text-sm font-medium text-gray-500 dark:text-gray-400 font-mono">{key}</Text>
                  <View className="text-right">{renderValue(comparison.left, comparison.isDifferent, "left")}</View>
                  <View className="text-right">{renderValue(comparison.right, comparison.isDifferent, "right")}</View>
                </View>

                {/* Mobile Layout */}
                <View className="md:hidden">
                  <Text className="text-sm font-medium text-gray-500 dark:text-gray-400 font-mono mb-2">{key}</Text>
                  <View className={clsx("space-y-2 p-3 rounded", comparison.isDifferent && "bg-gray-50 dark:bg-gray-700/30")}>
                    <View className="flex justify-between items-center">
                      <Text className="text-xs text-gray-500 dark:text-gray-400">Older:</Text>
                      {renderValue(comparison.left, comparison.isDifferent, "left")}
                    </View>
                    <View className="flex justify-between items-center">
                      <Text className="text-xs text-gray-500 dark:text-gray-400">Newer:</Text>
                      {renderValue(comparison.right, comparison.isDifferent, "right")}
                    </View>
                  </View>
                </View>
              </View>
            ))}
          </View>
        </View>
      )}

      {/* Function Version Comparison */}
      {getFunctionInfo && (
        <View className="mb-6">
          <Text className="text-lg font-medium text-gray-900 dark:text-white mb-4">Function Versions</Text>
          {(() => {
            const leftFunction = getFunctionInfo(leftDeployment.environmentId, leftDeployment);
            const rightFunction = getFunctionInfo(rightDeployment.environmentId, rightDeployment);

            const functionsAreDifferent = leftFunction?.name !== rightFunction?.name || leftFunction?.version !== rightFunction?.version;

            return (
              <View>
                {/* Desktop Layout */}
                <View
                  className={clsx(
                    "hidden md:grid grid-cols-3 gap-4 py-2 border-b border-gray-100 dark:border-gray-600",
                    functionsAreDifferent && "bg-gray-50 dark:bg-gray-700/30 -mx-2 px-2 rounded",
                  )}
                >
                  <Text className="text-sm font-medium text-gray-500 dark:text-gray-400">Function</Text>
                  <View className="text-right">
                    {leftFunction ? (
                      <View>
                        <Text
                          className={clsx(
                            "font-mono text-sm mr-2",
                            functionsAreDifferent ? "text-red-600 dark:text-red-400 font-medium" : "text-gray-900 dark:text-white",
                          )}
                        >
                          {leftFunction.name}
                        </Text>
                        {leftFunction.version && (
                          <Text className={clsx("text-xs", functionsAreDifferent ? "text-red-500 dark:text-red-300" : "text-gray-500 dark:text-gray-400")}>
                            {leftFunction.version}
                          </Text>
                        )}
                      </View>
                    ) : (
                      <Text className="text-gray-400 dark:text-gray-500 italic text-sm">No function</Text>
                    )}
                  </View>
                  <View className="text-right">
                    {rightFunction ? (
                      <View>
                        <Text
                          className={clsx(
                            "font-mono text-sm mr-2",
                            functionsAreDifferent ? "text-green-600 dark:text-green-400 font-medium" : "text-gray-900 dark:text-white",
                          )}
                        >
                          {rightFunction.name}
                        </Text>
                        {rightFunction.version && (
                          <Text className={clsx("text-xs", functionsAreDifferent ? "text-green-500 dark:text-green-300" : "text-gray-500 dark:text-gray-400")}>
                            {rightFunction.version}
                          </Text>
                        )}
                      </View>
                    ) : (
                      <Text className="text-gray-400 dark:text-gray-500 italic text-sm">No function</Text>
                    )}
                  </View>
                </View>

                {/* Mobile Layout */}
                <View className="md:hidden">
                  <Text className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Function</Text>
                  <View className={clsx("space-y-3 p-3 rounded", functionsAreDifferent && "bg-gray-50 dark:bg-gray-700/30")}>
                    <View className="flex justify-between items-start">
                      <Text className="text-xs text-gray-500 dark:text-gray-400">Older:</Text>
                      <View className="text-right">
                        {leftFunction ? (
                          <View>
                            <Text
                              className={clsx(
                                "font-mono text-sm mr-2",
                                functionsAreDifferent ? "text-red-600 dark:text-red-400 font-medium" : "text-gray-900 dark:text-white",
                              )}
                            >
                              {leftFunction.name}
                            </Text>
                            {leftFunction.version && (
                              <Text className={clsx("text-xs", functionsAreDifferent ? "text-red-500 dark:text-red-300" : "text-gray-500 dark:text-gray-400")}>
                                {leftFunction.version}
                              </Text>
                            )}
                          </View>
                        ) : (
                          <Text className="text-gray-400 dark:text-gray-500 italic text-sm">No function</Text>
                        )}
                      </View>
                    </View>
                    <View className="flex justify-between items-start">
                      <Text className="text-xs text-gray-500 dark:text-gray-400">Newer:</Text>
                      <View className="text-right">
                        {rightFunction ? (
                          <View>
                            <Text
                              className={clsx(
                                "font-mono text-sm mr-2",
                                functionsAreDifferent ? "text-green-600 dark:text-green-400 font-medium" : "text-gray-900 dark:text-white",
                              )}
                            >
                              {rightFunction.name}
                            </Text>
                            {rightFunction.version && (
                              <Text
                                className={clsx("text-xs", functionsAreDifferent ? "text-green-500 dark:text-green-300" : "text-gray-500 dark:text-gray-400")}
                              >
                                {rightFunction.version}
                              </Text>
                            )}
                          </View>
                        ) : (
                          <Text className="text-gray-400 dark:text-gray-500 italic text-sm">No function</Text>
                        )}
                      </View>
                    </View>
                  </View>
                </View>
              </View>
            );
          })()}
        </View>
      )}
    </motion.div>
  );
};
